/**
 * Admin tools: Activity Log and Maintenance (with login messages), plus the parts everyone sees:
 * the login message pop-up and birthday confetti.
 */
import { api } from "../api";
import { S, on, render } from "../state";
import { resetEvents } from "../events";
import { resetPeopleCache } from "./calendar";
import { $, closeSheet, edHead, esc, fmtDateTime, openSheet, toast } from "../ui";

/* ---------------- birthday confetti ---------------- */

/** 288 pieces with blue and red hearts, spread over 5 seconds (Peter, 2026-11-03). */
export function birthdayConfetti() {
  if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const w = document.createElement("div");
  w.className = "confetti";
  w.setAttribute("aria-hidden", "true");
  const cs = ["#1600FF", "#E10600", "#2654F9", "#9D050F", "#0614A8", "#010244"];
  for (let i = 0; i < 288; i++) {
    const c = document.createElement("i");
    c.style.left = Math.random() * 100 + "%";
    if (i % 3 === 0) {
      c.className = "heart";
      c.textContent = "♥";
      c.style.color = i % 2 ? "#1600FF" : "#E10600";
      c.style.fontSize = 16 + Math.random() * 14 + "px";
    } else c.style.background = cs[i % 6];
    c.style.animationDelay = Math.random() * 5 + "s";
    c.style.animationDuration = 1.6 + Math.random() * 1.2 + "s";
    w.appendChild(c);
  }
  document.body.appendChild(w);
  setTimeout(() => w.remove(), 9000);
}

/* ---------------- login message pop-up ---------------- */

export function showLoginMessage(m: { id: number; body: string; skipsLeft: number }) {
  const paras = m.body
    .split(/\n{2,}/)
    .map((p) => `<p style="margin:0 0 10px">${esc(p).replace(/\n/g, "<br>")}</p>`)
    .join("");
  openSheet(`${edHead("Message from Admissions")}<div class="edbody"><div>${paras}</div></div>
    <div class="edfoot"><button class="btn" data-act="lm-done" data-id="${m.id}">Got It</button>${
      m.skipsLeft > 0 ? `<button class="btn ghost" data-act="lm-skip" data-id="${m.id}">Skip for Now${m.skipsLeft === 1 ? " (Last Time)" : ""}</button>` : ""
    }</div>`);
}

/* ---------------- Maintenance tab ---------------- */

type Msg = { id: number; audience: "both" | "PA" | "SA"; body: string; active: boolean; createdAt: number; dismissed: number; seen: number };
const M: { msgs: Msg[] | null; log: any[] | null; logDone: boolean; loading: boolean } = { msgs: null, log: null, logDone: false, loading: false };
const AUD: Record<string, string> = { both: "PAs and SAs", PA: "Parent Ambassadors", SA: "Student Ambassadors" };

async function loadMsgs() {
  try {
    M.msgs = await api<Msg[]>("/api/site/login-messages");
  } catch (e) {
    toast((e as Error).message);
  }
  const box = $("#lmlist");
  if (box) box.innerHTML = msgsBody();
}

function msgsBody() {
  if (!M.msgs) return '<p class="muted">Loading…</p>';
  if (!M.msgs.length) return '<p class="muted small" style="margin:0">No login messages.</p>';
  return `<ul class="list">${M.msgs
    .map(
      (m) => `<li><div class="row between wrap" style="gap:8px;align-items:flex-start"><div style="min-width:0;flex:1"><div class="row wrap" style="gap:6px;margin-bottom:4px"><span class="chip ${m.active ? "ok" : ""}">${m.active ? "On" : "Off"}</span><span class="chip blue">${AUD[m.audience]}</span><span class="muted small">${m.dismissed} read · ${fmtDateTime(m.createdAt)}</span></div>
      <div class="small" style="white-space:pre-wrap">${esc(m.body)}</div></div><button class="btn ghost sm" data-act="lm-edit" data-id="${m.id}">Edit</button></div></li>`,
    )
    .join("")}</ul>`;
}

export function maintTab() {
  if (!M.msgs) void loadMsgs();
  const on = S.me!.maintenance;
  return `<div class="stack">
  <div class="card stack" style="gap:14px"><h2>Login Messages</h2>
    <p class="muted small" style="margin:0">Shown when a PA or SA opens the site. They can skip it twice, then must read it and tap Got It. Admissions sees every active message on their Home page.</p>
    <div id="lmlist">${msgsBody()}</div>
    <div><button class="btn sm" data-act="lm-new">Add Login Message</button></div></div>
  <div class="card"><div class="row between" style="gap:14px"><div><b>Maintenance Mode</b><div class="muted small">Shows a banner on every page, pauses sign-ups and changes for everyone but admins, and holds emails and push notifications. When it ends, held emails go out, except ones for events that already started.</div></div>
    <label class="toggle"><input type="checkbox" ${on ? "checked" : ""} data-change="maint-toggle" aria-label="Maintenance Mode"><span></span></label></div></div>
  <div class="card stack" style="gap:10px"><h2>Import Sign-Up Sheet</h2>
    <p class="muted small" style="margin:0">Loads the 2026-27 Tour List and 26-27 Completed tabs. Nothing is sent: no invites, emails or notifications. People without a profile get a placeholder one (peterp416+import-name@gmail.com). Running it again skips what's already here.</p>
    <div><button class="btn sm" data-act="imp-preview">Preview Import</button></div></div>
  </div>`;
}

function msgEditor(m?: Msg) {
  openSheet(`${edHead(m ? "Edit Login Message" : "Add Login Message")}<div class="edbody">
    <section class="edsec"><h3>Who Sees It</h3><select class="f" id="lmaud">${(["both", "PA", "SA"] as const).map((a) => `<option value="${a}" ${(m?.audience || "both") === a ? "selected" : ""}>${AUD[a]}</option>`).join("")}</select></section>
    <section class="edsec"><h3>Message</h3><textarea id="lmbody" rows="6" maxlength="2000">${esc(m?.body || "")}</textarea>
      ${m ? '<p class="muted small" style="margin:0">Changing the words shows it again to everyone, even people who already read it.</p>' : ""}</section>
    ${m ? `<section class="edsec"><h3>Status</h3><label class="row" style="gap:8px;cursor:pointer"><input type="checkbox" id="lmon" ${m.active ? "checked" : ""} style="width:18px;height:18px"> Turned On</label></section>` : ""}
    <div class="err" id="err" role="alert"></div></div>
    <div class="edfoot"><button class="btn" data-act="lm-save" ${m ? `data-id="${m.id}"` : ""}>Save</button>${m ? `<button class="btn ghost" data-act="lm-del" data-id="${m.id}">Delete</button>` : ""}<button class="btn ghost" data-act="close-sheet">Cancel</button></div>`);
}

type ImportSummary = {
  newPeople: { name: string; role: string }[];
  matchedPeople: number;
  newEvents: number;
  existingEvents: number;
  newSignups: number;
  pastSignups: number;
  warnings: string[];
  done: boolean;
};

function importSheetView(r: ImportSummary) {
  const people = r.newPeople.length
    ? `<ul class="list small">${r.newPeople.map((p) => `<li>${esc(p.name)} <span class="muted">· ${p.role}</span></li>`).join("")}</ul>`
    : '<p class="muted small" style="margin:0">None. Everyone already has a profile.</p>';
  const warn = r.warnings.length ? `<section class="edsec"><h3>Dates Checked</h3><ul class="list small">${r.warnings.map((w) => `<li>${esc(w)}</li>`).join("")}</ul></section>` : "";
  const nothing = !r.newEvents && !r.newSignups && !r.newPeople.length;
  openSheet(`${edHead(r.done ? "Sign-Up Sheet Imported" : "Import Sign-Up Sheet")}<div class="edbody">
    <section class="edsec"><h3>${r.done ? "Added" : "Will Add"}</h3><p style="margin:0">${r.newEvents} events (${r.existingEvents} already here), ${r.newSignups} sign-ups (${r.pastSignups} past, counted as checked in), ${r.newPeople.length} placeholder profiles (${r.matchedPeople} matched to existing profiles).</p></section>
    <section class="edsec"><h3>New Placeholder Profiles</h3>${people}</section>${warn}
    <p class="muted small" style="margin:0">Nothing is sent to anyone.</p></div>
    <div class="edfoot">${r.done || nothing ? '<button class="btn" data-act="close-sheet">Done</button>' : '<button class="btn" data-act="imp-run">Import Now</button><button class="btn ghost" data-act="close-sheet">Cancel</button>'}</div>`);
}

/* ---------------- Activity Log tab ---------------- */

async function loadLog(more = false) {
  if (M.loading) return;
  M.loading = true;
  try {
    const before = more && M.log?.length ? M.log[M.log.length - 1].id : "";
    const rows = await api<any[]>(`/api/site/activity${before ? `?before=${before}` : ""}`);
    M.log = more ? [...(M.log || []), ...rows] : rows;
    M.logDone = rows.length < 100;
  } catch (e) {
    toast((e as Error).message);
  }
  M.loading = false;
  const box = $("#loglist");
  if (box) box.innerHTML = logBody();
}

function logBody() {
  if (!M.log) return '<p class="muted">Loading…</p>';
  if (!M.log.length) return '<p class="muted">Nothing yet.</p>';
  return `<div class="tablewrap"><table><thead><tr><th>When</th><th>Who</th><th>What</th></tr></thead><tbody>${M.log
    .map((l) => `<tr><td class="small" style="white-space:nowrap">${fmtDateTime(l.at)}</td><td><b>${esc(l.who)}</b></td><td>${esc(l.text)}${l.target ? `: <span class="muted">${esc(l.target)}</span>` : ""}</td></tr>`)
    .join("")}</tbody></table></div>${M.logDone ? "" : '<div style="margin-top:12px"><button class="btn ghost sm" data-act="log-more">Show More</button></div>'}`;
}

export function logTab() {
  if (!M.log) void loadLog();
  return `<div class="card"><p class="muted small" style="margin:0 0 10px">Newest first. Kept for 3 school years.</p><div id="loglist">${logBody()}</div></div>`;
}

export const resetSiteTools = () => {
  M.log = null;
  M.msgs = null;
};

on({
  "lm-skip": async (el) => {
    closeSheet();
    await api(`/api/me/login-message/${el.dataset.id}`, { body: { action: "skip" } }).catch((e) => toast((e as Error).message));
  },
  "lm-done": async (el) => {
    closeSheet();
    await api(`/api/me/login-message/${el.dataset.id}`, { body: { action: "dismiss" } }).catch((e) => toast((e as Error).message));
  },
  "lm-new": () => msgEditor(),
  "lm-edit": (el) => msgEditor(M.msgs?.find((m) => m.id === Number(el.dataset.id))),
  "lm-save": async (el) => {
    const body = { audience: ($("#lmaud") as HTMLSelectElement).value, body: ($("#lmbody") as HTMLTextAreaElement).value, active: ($("#lmon") as HTMLInputElement | null)?.checked ?? true };
    try {
      if (el.dataset.id) await api(`/api/site/login-messages/${el.dataset.id}`, { method: "PATCH", body });
      else await api("/api/site/login-messages", { body });
      closeSheet();
      toast("Saved.");
      void loadMsgs();
    } catch (e) {
      $("#err")!.textContent = (e as Error).message;
    }
  },
  "lm-del": async (el) => {
    const b = el as HTMLButtonElement;
    if (b.dataset.sure !== "1") {
      b.dataset.sure = "1";
      b.textContent = "Tap Again to Delete";
      return;
    }
    try {
      await api(`/api/site/login-messages/${el.dataset.id}`, { method: "DELETE" });
      closeSheet();
      toast("Deleted.");
      void loadMsgs();
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "maint-toggle": (el) => {
    const box = el as HTMLInputElement;
    const turnOn = box.checked;
    box.checked = !turnOn; // only changes after they confirm
    openSheet(`${edHead(turnOn ? "Turn On Maintenance Mode?" : "Turn Off Maintenance Mode?")}<div class="edbody"><p style="margin:0">${
      turnOn
        ? "Everyone will see the maintenance banner. Only admins can change anything, and emails and push notifications wait until you turn it off."
        : "Sign-ups open again, and held emails go out now. Emails for events that already started are dropped."
    }</p></div>
      <div class="edfoot"><button class="btn ${turnOn ? "danger" : ""}" data-act="maint-set" data-on="${turnOn ? 1 : 0}">${turnOn ? "Turn On" : "Turn Off"}</button><button class="btn ghost" data-act="close-sheet">Cancel</button></div>`);
  },
  "maint-set": async (el) => {
    try {
      const r = await api<{ on: boolean }>("/api/site/maintenance", { method: "PUT", body: { on: el.dataset.on === "1" } });
      closeSheet();
      S.me!.maintenance = r.on;
      toast(r.on ? "Maintenance Mode is on." : "Maintenance Mode is off. Held emails are sending.");
      render();
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "imp-preview": async () => {
    try {
      importSheetView(await api<ImportSummary>("/api/site/import-sheet"));
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "imp-run": async (el) => {
    (el as HTMLButtonElement).disabled = true;
    el.textContent = "Importing…";
    try {
      importSheetView(await api<ImportSummary>("/api/site/import-sheet", { body: {} }));
      M.log = null;
      // Show the new events and people without a reload.
      resetEvents();
      resetPeopleCache();
    } catch (e) {
      toast((e as Error).message);
      (el as HTMLButtonElement).disabled = false;
      el.textContent = "Import Now";
    }
  },
  "log-more": () => void loadLog(true),
  "bday-replay": () => birthdayConfetti(),
});
